import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./AddOrganization.css";
import OrganizationTabs from "./OrganizationTabs";

const emptyForm = {
  country_code: "",
  region_name: "",
  industry: "",
  effective_from: "",
  effective_to: "",
  status: "ACTIVE",
  is_registration_enabled: true,
  is_active: true,
};

const AddTenantOperation = () => {
  const [operations, setOperations] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptyForm }]);
  const [tenantId, setTenantId] = useState(null);

  // ----------------------------------------------------------
  // GET TENANT ID
  // ----------------------------------------------------------

  useEffect(() => {
    const storedTenantUser = localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      Swal.fire({
        icon: "error",
        title: "Tenant information missing",
        text: "Please login again.",
      });

      return;
    }

    try {
      const tenantUser = JSON.parse(storedTenantUser);

      if (!tenantUser.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant ID missing",
          text: "Tenant information could not be found.",
        });

        return;
      }

      setTenantId(Number(tenantUser.tenant_id));
    } catch (error) {
      console.error("Invalid tenant_user:", error);

      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again.",
      });
    }
  }, []);

  // ----------------------------------------------------------
  // FETCH DATA
  // ----------------------------------------------------------

  useEffect(() => {
    if (tenantId) {
      fetchOperations();
      fetchIndustries();
    }
  }, [tenantId]);

  // ----------------------------------------------------------
  // FETCH TENANT OPERATIONS
  // ----------------------------------------------------------

const fetchOperations = async () => {
  try {
    setLoading(true);

    const response = await axios.post(`${API_BASE}/tenant-records/`, {
      tenant_id: Number(tenantId),
      tables: ["tenant_operations"],
    });

    console.log("Tenant operations response:", response.data);

    let data = response.data;

    if (response.data?.data) {
      data = response.data.data;
    }

    // Handle table-wise response
    if (data?.tenant_operations) {
      data = data.tenant_operations;
    }

    if (!Array.isArray(data)) {
      data = [];
    }

    setOperations(data);
  } catch (error) {
    console.error(
      "Error fetching tenant operations:",
      error
    );

    Swal.fire({
      icon: "error",
      title: "Unable to load operations",
      text:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Something went wrong while loading tenant operations.",
    });
  } finally {
    setLoading(false);
  }
};
  // ----------------------------------------------------------
  // FETCH INDUSTRIES
  // ----------------------------------------------------------

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(
        `${API_BASE}/reference-values/`
      );

      let data = response.data;

      if (response.data?.data) {
        data = response.data.data;
      }

      if (!Array.isArray(data)) {
        data = [];
      }

      const industryValues = data
        .filter(
          (item) =>
            item.option_set_value?.option_type === "INDUSTRY" &&
            item.is_active
        )
        .map((item) => ({
          id: item.id,
          code: item.code,
          label: item.label,
        }));

      setIndustries(industryValues);
    } catch (error) {
      console.error("Error fetching industries:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to load industries",
        text: "Industry values could not be loaded.",
      });
    }
  };

  // ----------------------------------------------------------
  // HANDLE FORM CHANGE
  // ----------------------------------------------------------

  const handleChange = (index, e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) =>
      prev.map((operation, operationIndex) =>
        operationIndex === index
          ? {
              ...operation,
              [name]:
                type === "checkbox" ? checked : value,
            }
          : operation
      )
    );
  };

  // ----------------------------------------------------------
  // ADD OPERATION
  // ----------------------------------------------------------

  const handleAdd = () => {
    setFormData([
      {
        ...emptyForm,
        status: "ACTIVE",
        is_registration_enabled: true,
        is_active: true,
      },
    ]);

    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // ADD ANOTHER OPERATION
  // ----------------------------------------------------------

  const handleAddAnother = () => {
    setFormData((prev) => [
      ...prev,
      {
        ...emptyForm,
        status: "ACTIVE",
        is_registration_enabled: true,
        is_active: true,
      },
    ]);
  };

  // ----------------------------------------------------------
  // REMOVE OPERATION FORM
  // ----------------------------------------------------------

  const handleRemoveOperationForm = (index) => {
    setFormData((prev) =>
      prev.filter(
        (_, operationIndex) => operationIndex !== index
      )
    );
  };

  // ----------------------------------------------------------
  // EDIT OPERATION
  // ----------------------------------------------------------

  const handleEdit = (operation) => {
    setFormData([
      {
        country_code: operation.country_code || "",
        region_name: operation.region_name || "",
        industry: operation.industry || "",
        effective_from: operation.effective_from || "",
        effective_to: operation.effective_to || "",
        status: operation.status || "ACTIVE",
        is_registration_enabled:
          operation.is_registration_enabled !== undefined
            ? operation.is_registration_enabled
            : true,
        is_active:
          operation.is_active !== undefined
            ? operation.is_active
            : true,
      },
    ]);

    setEditingId(operation.id);
    setIsEditing(true);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // DELETE OPERATION
  // ----------------------------------------------------------

  const handleDelete = async (operation) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Operation?",
      text: `Are you sure you want to delete the operation for "${
        operation.region_name || operation.country_code
      }"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE}/tenant-operations/${operation.id}/`
      );

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Tenant operation deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchOperations();
    } catch (error) {
      console.error(
        "Error deleting tenant operation:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the tenant operation.",
      });
    }
  };

  // ----------------------------------------------------------
  // SUBMIT FORM
  // ----------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant.",
      });

      return;
    }

    // Validate all forms
    for (let i = 0; i < formData.length; i++) {
      const operation = formData[i];

      if (!operation.country_code) {
        Swal.fire({
          icon: "warning",
          title: "Country Code Required",
          text: `Please enter Country Code for Operation ${
            i + 1
          }.`,
        });

        return;
      }

      if (
        operation.effective_from &&
        operation.effective_to &&
        operation.effective_to <
          operation.effective_from
      ) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Date Range",
          text: `Effective To date cannot be earlier than Effective From date for Operation ${
            i + 1
          }.`,
        });

        return;
      }
    }

    try {
      setLoading(true);

      // ------------------------------------------------------
      // UPDATE
      // ------------------------------------------------------

      if (isEditing) {
        const operation = formData[0];

        const payload = {
          country_code: operation.country_code,
          region_name: operation.region_name,
          industry: operation.industry
            ? Number(operation.industry)
            : null,
          status: operation.status,
          is_registration_enabled:
            operation.is_registration_enabled,
          is_active: operation.is_active,
          effective_from:
            operation.effective_from || null,
          effective_to:
            operation.effective_to || null,
          tenant: Number(tenantId),
        };

        await axios.put(
          `${API_BASE}/tenant-operations/${editingId}/`,
          payload
        );

        await Swal.fire({
          icon: "success",
          title: "Operation Updated",
          text: "Tenant operation updated successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      // ------------------------------------------------------
      // CREATE MULTIPLE OPERATIONS
      // ------------------------------------------------------

      else {
        for (const operation of formData) {
          const payload = {
            country_code: operation.country_code,
            region_name: operation.region_name,
            industry: operation.industry
              ? Number(operation.industry)
              : null,
            status: operation.status,
            is_registration_enabled:
              operation.is_registration_enabled,
            is_active: operation.is_active,
            effective_from:
              operation.effective_from || null,
            effective_to:
              operation.effective_to || null,
            tenant: Number(tenantId),
          };

          await axios.post(
            `${API_BASE}/tenant-operations/`,
            payload
          );
        }

        await Swal.fire({
          icon: "success",
          title: "Operations Created",
          text: `${formData.length} operation${
            formData.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyForm }]);

      await fetchOperations();
    } catch (error) {
      console.error(
        "Error saving tenant operations:",
        error
      );

      const backendError =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data ||
        "Unable to save tenant operations.";

      Swal.fire({
        icon: "error",
        title: isEditing
          ? "Update Failed"
          : "Creation Failed",
        text:
          typeof backendError === "string"
            ? backendError
            : JSON.stringify(backendError),
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // CANCEL
  // ----------------------------------------------------------

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyForm }]);
  };

  // ----------------------------------------------------------
  // INDUSTRY LABEL
  // ----------------------------------------------------------

  const getIndustryLabel = (industryId) => {
    if (!industryId) {
      return "-";
    }

    const industry = industries.find(
      (item) =>
        Number(item.id) === Number(industryId)
    );

    return industry
      ? `${industry.code} - ${industry.label}`
      : `Industry #${industryId}`;
  };

  // ----------------------------------------------------------
  // DATE FORMAT
  // ----------------------------------------------------------

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-GB");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="organization-page">
          <OrganizationTabs/>
          {!showForm ? (
            <>
              {/* LIST HEADER */}

              <div className="organization-page-header">
                <div>
                  <h2>Operations</h2>
                </div>

                <button
                  type="button"
                  className="organization-add-btn"
                  onClick={handleAdd}
                >
                  + Add Operation
                </button>
              </div>

              {/* TABLE */}

              <div className="organization-list-card">
                {loading ? (
                  <div className="organization-loading">
                    Loading operations...
                  </div>
                ) : operations.length === 0 ? (
                  <div className="organization-empty">
                    <div className="organization-empty-icon">
                      +
                    </div>

                    <h3>No Operations Found</h3>

                    <p>
                      There are no operations available for
                      the current tenant.
                    </p>

                    <button
                      type="button"
                      className="organization-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Operation
                    </button>
                  </div>
                ) : (
                  <div className="organization-table-wrapper">
                    <table className="organization-table">
                      <thead>
                        <tr>
                          <th>Country</th>
                          <th>Region</th>
                          <th>Industry</th>
                          <th>Effective From</th>
                          <th>Effective To</th>
                          <th>Registration</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {operations.map((operation) => (
                          <tr key={operation.id}>
                            <td>
                              <strong>
                                {operation.country_code ||
                                  "-"}
                              </strong>
                            </td>

                            <td>
                              {operation.region_name || "-"}
                            </td>

                            <td>
                              {getIndustryLabel(
                                operation.industry
                              )}
                            </td>

                            <td>
                              {formatDate(
                                operation.effective_from
                              )}
                            </td>

                            <td>
                              {formatDate(
                                operation.effective_to
                              )}
                            </td>

                            <td>
                              <span
                                className={`organization-status ${
                                  operation.is_registration_enabled
                                    ? "active"
                                    : "inactive"
                                }`}
                              >
                                {operation.is_registration_enabled
                                  ? "Enabled"
                                  : "Disabled"}
                              </span>
                            </td>

                            <td>
                              <span
                                className={`organization-status ${
                                  operation.is_active
                                    ? "active"
                                    : "inactive"
                                }`}
                              >
                                {operation.is_active
                                  ? "Active"
                                  : "Inactive"}
                              </span>
                            </td>

                            <td>
                              <div className="organization-actions">
                                <button
                                  type="button"
                                  className="organization-edit-btn"
                                  onClick={() =>
                                    handleEdit(operation)
                                  }
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="organization-delete-btn"
                                  onClick={() =>
                                    handleDelete(
                                      operation
                                    )
                                  }
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ADD / EDIT HEADER */}

              <div className="organization-page-header">
                <div>
                  <h2>
                    {isEditing
                      ? "Edit Operation"
                      : "Create Operations"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="organization-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* FORM */}

              <form onSubmit={handleSubmit}>
                {formData.map((operation, index) => (
                  <div
                    className="organization-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="organization-form-body">
                      <div
                        className="organization-section-title"
                        style={{
                          display: "flex",
                          justifyContent:
                            "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          Operation {index + 1}
                        </span>

                        {!isEditing &&
                          formData.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveOperationForm(
                                  index
                                )
                              }
                              style={{
                                border: "none",
                                background:
                                  "transparent",
                                color: "#dc2626",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: "600",
                              }}
                            >
                              Remove
                            </button>
                          )}
                      </div>

                      <div className="organization-form-grid">
                        {/* COUNTRY CODE */}

                        <div className="organization-form-group">
                          <label>
                            Country Code{" "}
                            <span>*</span>
                          </label>

                          <input
                            type="text"
                            name="country_code"
                            value={
                              operation.country_code
                            }
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                            placeholder="e.g. IN"
                            maxLength={2}
                            required
                          />
                        </div>

                        {/* REGION NAME */}

                        <div className="organization-form-group">
                          <label>
                            Region Name
                          </label>

                          <input
                            type="text"
                            name="region_name"
                            value={
                              operation.region_name
                            }
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                            placeholder="Enter region name"
                            maxLength={150}
                          />
                        </div>

                        {/* INDUSTRY */}

                        <div className="organization-form-group">
                          <label>Industry</label>

                          <select
                            name="industry"
                            value={
                              operation.industry
                            }
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                          >
                            <option value="">
                              Select industry
                            </option>

                            {industries.map(
                              (industry) => (
                                <option
                                  key={industry.id}
                                  value={industry.id}
                                >
                                  {industry.code} -{" "}
                                  {industry.label}
                                </option>
                              )
                            )}
                          </select>
                        </div>

                        {/* EFFECTIVE FROM */}

                        <div className="organization-form-group">
                          <label>
                            Effective From
                          </label>

                          <input
                            type="date"
                            name="effective_from"
                            value={
                              operation.effective_from
                            }
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                          />
                        </div>

                        {/* EFFECTIVE TO */}

                        <div className="organization-form-group">
                          <label>
                            Effective To
                          </label>

                          <input
                            type="date"
                            name="effective_to"
                            value={
                              operation.effective_to
                            }
                            onChange={(e) =>
                              handleChange(index, e)
                            }
                          />
                        </div>

                        {/* OPERATION STATUS */}

                        <div className="organization-status-field">
                          <label>
                            Operation Status
                          </label>

                          <label className="organization-switch">
                            <input
                              type="checkbox"
                              name="is_active"
                              checked={
                                operation.is_active
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                            />

                            <span className="organization-slider"></span>
                          </label>

                          <span className="organization-status-text">
                            {operation.is_active
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </div>

                        {/* REGISTRATION ENABLED */}

                        <div className="organization-status-field">
                          <label>
                            Registration Enabled
                          </label>

                          <label className="organization-switch">
                            <input
                              type="checkbox"
                              name="is_registration_enabled"
                              checked={
                                operation.is_registration_enabled
                              }
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                            />

                            <span className="organization-slider"></span>
                          </label>

                          <span className="organization-status-text">
                            {operation.is_registration_enabled
                              ? "Enabled"
                              : "Disabled"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* ADD ANOTHER OPERATION */}

                {!isEditing && (
                  <button
                    type="button"
                    className="organization-add-btn"
                    onClick={handleAddAnother}
                    style={{
                      marginBottom: "20px",
                    }}
                  >
                    + Add Another Operation
                  </button>
                )}

                {/* FORM ACTIONS */}

                <div className="organization-form-actions">
                  <button
                    type="button"
                    className="organization-cancel-btn"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="organization-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Operation"
                      : "Save Operations"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantOperation;